import { useState } from 'react'
import {
  Alert,
  Badge,
  Button,
  Group,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import dayjs from 'dayjs'
import { Link } from 'react-router-dom'
import {
  useExpandirOcorrencias,
  useGerarOSPorRecorte,
  usePreviewRecorte,
} from '../../hooks/useOcorrencias'
import { CATEGORIAS_PLANO, labelDe } from '../planos_modelo/constants'
import { extractApiError } from '../../lib/extractApiError'
import type { PMOC } from '../../types'
import type {
  GerarOSRecorteResponse,
  PreviewRecorteResponse,
  RecortePayload,
} from '../../types/pmocPlanos'

interface Props {
  pmoc: PMOC
}

export default function PmocGerarOSTab({ pmoc }: Props) {
  const hoje = dayjs()
  const [form, setForm] = useState<RecortePayload>({
    data_inicial: hoje.format('YYYY-MM-DD'),
    data_final: hoje.add(30, 'day').format('YYYY-MM-DD'),
    granularidade: 'equipamento',
    somente_pendentes: true,
    somente_vencidas: false,
  })
  const [preview, setPreview] = useState<PreviewRecorteResponse | null>(null)
  const [resultadoGeracao, setResultadoGeracao] =
    useState<GerarOSRecorteResponse | null>(null)

  const previewMut = usePreviewRecorte(pmoc.id)
  const gerar = useGerarOSPorRecorte(pmoc.id)
  const expandir = useExpandirOcorrencias(pmoc.id)

  async function handlePreview() {
    setResultadoGeracao(null)
    try {
      await expandir.mutateAsync({
        data_inicial: form.data_inicial,
        data_final: form.data_final,
        plano_pmoc_id: form.plano_pmoc_id,
      })
      const r = await previewMut.mutateAsync(form)
      setPreview(r)
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Falha no preview',
        message: extractApiError(err, 'Erro ao gerar pré-visualização.'),
      })
    }
  }

  async function handleGerar() {
    if (!preview || preview.total_ocorrencias === 0) return
    if (
      !confirm(
        `Gerar ${preview.grupos.length} OS cobrindo ${preview.total_ocorrencias} ocorrências?`,
      )
    )
      return
    try {
      const r = await gerar.mutateAsync(form)
      setResultadoGeracao(r)
      setPreview(null)
      notifications.show({
        color: 'green',
        title: `${r.total} OS criadas`,
        message: 'Cada grupo do recorte virou uma OS.',
      })
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Falha ao gerar',
        message: extractApiError(err, 'Erro ao gerar OS.'),
      })
    }
  }

  return (
    <Stack gap="md">
      <Title order={4}>Gerar OS por recorte</Title>
      <Text size="xs" c="dimmed">
        Recorte = filtro de ocorrências + granularidade (equipamento, ambiente ou
        site). Cada grupo do recorte vira uma OS agrupando as ocorrências dele.
      </Text>

      <Paper p="sm" withBorder>
        <Stack gap="xs">
          <Group grow>
            <TextInput
              label="Data inicial"
              type="date"
              value={form.data_inicial}
              onChange={(e) =>
                setForm({ ...form, data_inicial: e.currentTarget.value })
              }
            />
            <TextInput
              label="Data final"
              type="date"
              value={form.data_final}
              onChange={(e) =>
                setForm({ ...form, data_final: e.currentTarget.value })
              }
            />
            <Select
              label="Granularidade"
              data={[
                { value: 'equipamento', label: 'Equipamento (1 OS por equip.)' },
                { value: 'ambiente', label: 'Ambiente (1 OS por ambiente)' },
                { value: 'site', label: 'Site (1 OS por site)' },
              ]}
              value={form.granularidade ?? 'equipamento'}
              onChange={(v) =>
                v &&
                setForm({
                  ...form,
                  granularidade: v as 'equipamento' | 'ambiente' | 'site',
                })
              }
            />
          </Group>
          <Group grow>
            <Select
              label="Categoria"
              placeholder="Todas"
              clearable
              data={CATEGORIAS_PLANO}
              value={form.categoria ?? null}
              onChange={(v) => setForm({ ...form, categoria: v })}
            />
            <Switch
              label="Somente pendentes"
              checked={form.somente_pendentes ?? true}
              onChange={(e) =>
                setForm({ ...form, somente_pendentes: e.currentTarget.checked })
              }
            />
            <Switch
              label="Somente vencidas"
              checked={form.somente_vencidas ?? false}
              onChange={(e) =>
                setForm({ ...form, somente_vencidas: e.currentTarget.checked })
              }
            />
          </Group>
          <Group justify="flex-end">
            <Button
              onClick={handlePreview}
              loading={previewMut.isPending || expandir.isPending}
              variant="default"
              data-testid="preview-recorte-btn"
            >
              Pré-visualizar
            </Button>
            <Button
              onClick={handleGerar}
              loading={gerar.isPending}
              disabled={!preview || preview.total_ocorrencias === 0}
              data-testid="gerar-os-recorte-btn"
            >
              Gerar OS ({preview?.total_ocorrencias ?? 0} ocorr.)
            </Button>
          </Group>
        </Stack>
      </Paper>

      {resultadoGeracao && (
        <Alert color="green" variant="light">
          <Text fw={600}>
            {resultadoGeracao.total} OS criadas.
          </Text>
          <Group gap="xs" mt="xs" wrap="wrap">
            {resultadoGeracao.ordens_servico.map((ordem) => (
              <Link
                key={ordem.id}
                to={`/ordens-servico/${ordem.id}`}
                style={{ fontSize: '0.85rem' }}
              >
                OS #{ordem.numero} →
              </Link>
            ))}
          </Group>
        </Alert>
      )}

      {preview && (
        <Paper p="sm" withBorder>
          <Group justify="space-between" mb="xs">
            <Text fw={600}>
              Preview: {preview.grupos.length} grupos · {preview.total_ocorrencias}{' '}
              ocorrências
            </Text>
          </Group>
          {preview.grupos.length === 0 ? (
            <Text c="dimmed" size="sm">
              Nenhuma ocorrência atende ao recorte selecionado.
            </Text>
          ) : (
            <Table striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Grupo (futura OS)</Table.Th>
                  <Table.Th>Ocorrências</Table.Th>
                  <Table.Th>Datas</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {preview.grupos.map((g) => {
                  const datas = [...g.datas].sort()
                  const ini = datas[0]
                  const fim = datas[datas.length - 1]
                  return (
                    <Table.Tr key={`${g.categoria}-${g.alvo_id}`}>
                      <Table.Td>{g.granularidade}: {g.alvo_id.slice(0, 8)} · {g.tipo_os}</Table.Td>
                      <Table.Td>
                        <Badge variant="light">{g.total}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" c="dimmed">
                          {ini === fim ? ini : `${ini} → ${fim}`}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  )
                })}
              </Table.Tbody>
            </Table>
          )}
        </Paper>
      )}

      {/* dica usa labelDe pra confirmar import */}
      <Text size="xs" c="dimmed">
        Dica: filtre por categoria (ex.: {labelDe('higienizacao', CATEGORIAS_PLANO)})
        para gerar OS específicas de cada tipo de visita.
      </Text>
    </Stack>
  )
}
